import type {ImportSelecting, ImportState} from '@gaia/types';
import {ampli} from '@gaia/utils/gaia';
import {useEffect} from 'react';

import {useLatest} from './useLatest';

/**
 * This hook's logic is tricky and potentially confusing:
 *
 * 1. Runs, exactly once, when `importState.status` changes to `'selecting'`.
 * 2. Repeated re-renders while `importState.status === 'selecting'` do not cause this hook
 *    to run again.
 * 3. Only after `importState.status` has changed to something else and then changed back to
 *    `'selecting'` again does the effect run again, exactly once.
 * 4. And so on.
 *
 * A better way is a side-effect of a reducer action that transitions `importState` to the
 * `'selecting'` state.
 */
export function useImportAnalyticsEffect(importState: ImportState) {
    const latestImportState = useLatest(importState);

    const status = importState.status;

    useEffect(() => {
        if (status !== 'selecting') return;

        const is = latestImportState.current as ImportSelecting;

        // should a file selection abort be tracked?
        if (is.fileNames.length === 0) return;

        ampli.importData({
            type: is.eventSource,
            error: !!is.error,
            error_message: is.error?.detail,
            extension: is.fileNames.at(0)?.split('.').at(-1)?.toLowerCase(),
            files_imported: is.fileNames.length,
            waypoints: is.imports?.filter((d) => d.type === 'waypoint').length,
            tracks: is.imports?.filter((d) => d.type === 'track').length,
            areas: is.imports?.filter((d) => d.type === 'area').length,
        });
    }, [latestImportState, status]);
}
